/* eslint-disable react/jsx-curly-newline */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { groupBy, isEqual } from 'lodash';
import {
  Button,
  Label,
  EmptyState,
  Accordion,
  MultiSelect,
  CounterTag,
  Checkbox,
  Spinner,
} from '@gsa/afp-component-library';
import { useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { useEditSolicitation } from '../../components/apis/edit-solicitation-provider';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';
import '../../styles/select-standard.scss';
import { ADD_STANDARD_ITEMS_TO_SOLICITATION } from '../../queries';
import SinSelectionHeader from './sin-selection-header';
import SolicitationProgramMultiselect from './solicitaion-program-multiselect';
import { isSolicitationInDraft } from '../../components/solicitation-stepper';
import {
  getNextPath,
  genRevertedSelections,
  mergeSelections,
} from './sin-selection-helpers';

const EditSelectStandardItemWrapper = () => {
  const navigate = useNavigate();
  const [selectedPrograms, setSelectedPrograms] = useState(null);

  const {
    sinClassificationLoading,
    solicitationLoading,
    programDropdownOptions,
    vehicleClassifications,
    standardItems,
    solicitation,
    solicitationID,
    refetchSolicitation,
  } = useEditSolicitation();

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedVehicleGroup, setSelectedVehicleGroup] = useState(null);
  const [selectedStandardItems, setSelectedStandardItems] = useState(null);
  const [selections, setSelections] = useState({});
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  const [addStandardItemstoSolicitation] = useMutation(
    ADD_STANDARD_ITEMS_TO_SOLICITATION,
  );

  // the options resolve after the solicitation, so rerun when they arrive
  useEffect(() => {
    if (solicitation) {
      const { programs } = solicitation;
      const selectedProgramsList = [];
      programs.forEach((item) => {
        const programCode = item.program;
        const option = programDropdownOptions.find(
          (o) => o.label === programCode,
        );
        if (option) {
          selectedProgramsList.push(option.value);
        }
      });
      setSelectedPrograms((prev) =>
        isEqual(prev, selectedProgramsList) ? prev : selectedProgramsList,
      );
    }
  }, [solicitation, programDropdownOptions]);

  useEffect(() => {
    if (solicitation) {
      const currentSelections = {};
      const { programs } = solicitation;
      programs.forEach((item) => {
        const { solicitationLines } = item;

        solicitationLines.forEach((line) => {
          const standardItem = standardItems.find(
            (si) => Number(si.id) === Number(line.standardItemId),
          );

          if (standardItem) {
            // this si is still active and needs to be pre populated.
            const { code } = standardItem.fedStandard;
            if (currentSelections[code]) {
              const newSel = [...currentSelections[code], standardItem.id];
              currentSelections[code] = newSel;
            } else {
              currentSelections[code] = [standardItem.id];
            }
          }
        });
      });
      // bail out when nothing changed, otherwise a new standardItems identity
      // on every render would re-render forever
      setSelections((prev) =>
        isEqual(prev, currentSelections) ? prev : currentSelections,
      );
    }
    // both are needed: whichever resolves last has to rebuild the selections
  }, [standardItems, solicitation]);

  const getProgramNameForVehicleGroup = (vehicleGroup) => {
    const programName = vehicleClassifications.find(
      (item) => item.value === vehicleGroup,
    )?.parent;
    return programName;
  };

  useEffect(() => {
    const countOfAllSI = Object.values(selections).reduce((acc, curr) => {
      return acc + curr.length;
    }, 0);
    setSelectedStandardItems(countOfAllSI);
  }, [selections]);

  useEffect(() => {
    // list of all possible standard items
    const possibleValues = standardItems?.filter(
      (item) => item.fedStandard.code === selectedVehicleGroup,
    );
    // if the length matches we select the checkbox
    if (possibleValues?.length === selections[selectedVehicleGroup]?.length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  }, [selectedVehicleGroup, selections]);

  const handleSelectStandardItem = (item, vehicleTypeCode) => {
    const origSelections = { ...selections };
    const originalSI = origSelections[vehicleTypeCode] || [];
    const newSelections = originalSI.includes(item)
      ? originalSI.filter((i) => i !== item)
      : [...originalSI, item];
    origSelections[vehicleTypeCode] = newSelections;
    setSelections(origSelections);
  };

  const onSave = (goNext) => {
    setSolicitationNotification([]);

    // No need to force sin selection, since solicitation can be created as draft without sin
    const lines = [];
    Object.entries(selections).forEach(([key, value]) => {
      lines.push({
        program: getProgramNameForVehicleGroup(key),
        selectedStandardItemIds: value.map(Number),
      });
    });
    selectedPrograms.forEach((prog) => {
      const progName = prog.split('-')[0].trim();
      if (!lines.some((l) => l.program === progName))
        lines.push({
          program: progName,
          selectedStandardItemIds: [],
        });
    });
    const lineCount = lines.reduce((acc, curr) => {
      return acc + curr.selectedStandardItemIds.length;
    }, 0);
    if (!isSolicitationInDraft(solicitation) && lineCount === 0) {
      setSolicitationNotification([
        {
          id: 'CREATE_SOLICITATION',
          message: `There are no SINs associated with the solicitation. Add at least one SIN to the solicitation in order to save and continue.`,
          type: 'error',
          closeable: true,
          showInModal: false,
        },
      ]);
    } else {
      void addStandardItemstoSolicitation({
        variables: {
          selections: {
            solicitationId: Number(solicitationID),
            lines,
          },
        },
        onCompleted: ({ addOrUpdateSINSelections: response }) => {
          if (response.success) {
            const { id, solicitationNumber } = solicitation;
            setSolicitationNotification([
              {
                id: 'CREATE_SOLICITATION',
                message: `Solicitation ${solicitationNumber} updated successfully.`,
                type: 'success',
                closeable: true,
                showInModal: false,
                showOnNextPage: true,
              },
            ]);
            if (goNext) {
              const subpath = getNextPath(true, solicitation.bidType);
              navigate(`/catalog/solicitations/edit/${id}${subpath}`);
            } else {
              refetchSolicitation();
            }
          } else {
            const undoItems = genRevertedSelections(
              response.invalidSelections,
              standardItems,
              vehicleClassifications,
            );
            setSolicitationNotification([
              {
                id: 'CREATE_SOLICITATION',
                message: (
                  <div className="margin-right-8">
                    The standard item cannot be removed from the solicitation
                    because there is already a contract line for this SIN on the
                    solicitation/year. Please review your selections, and then
                    click next to continue.
                    {undoItems.map((item) => (
                      <li key={item.standardItemId} className="margin-left-4">
                        {item.program} - {item.fedStdCode} - {item.vehType}:{' '}
                        {item.sin} - {item.title}
                      </li>
                    ))}
                  </div>
                ),
                type: 'error',
                closeable: true,
                showInModal: false,
              },
            ]);
            // add back selections that cannot be removed
            setSelections((prev) => {
              return mergeSelections(prev, undoItems);
            });
          }
        },
      });
    }
  };

  const onClickPrev = () => {
    setSolicitationNotification([]);
    const subpath = getNextPath(false, solicitation.bidType);
    navigate(`/catalog/solicitations/edit/${solicitationID}${subpath}`);
  };

  // a derivation, not state: as an effect it re-renders on every new
  // standardItems identity and can exceed the update depth
  const groupedStandardItems = useMemo(() => {
    const filteredSIByVehicleGroup = standardItems.filter((item) => {
      return item.fedStandard.code === selectedVehicleGroup;
    });

    return groupBy(filteredSIByVehicleGroup, 'vehicleTypeCode.code');
  }, [selectedVehicleGroup, standardItems]);

  const handleSelectAllChange = (checkedStatus) => {
    setSolicitationNotification([]);
    const currentSelections = { ...selections };
    // if the select all is selected add an entry to selections with all the si in the vehicle group
    if (checkedStatus) {
      const siToSelect = standardItems.filter((item) => {
        return item.fedStandard.code === selectedVehicleGroup;
      });
      const newSelections = siToSelect.map((item) => item.id);
      currentSelections[selectedVehicleGroup] = newSelections;
      setSelections(currentSelections);
    } else {
      // if select all is unchecked remove the selected SINs of the selected vehicle group
      currentSelections[selectedVehicleGroup] = [];
      setSelections(currentSelections);
    }
  };
  const handleProgramDropdownChange = (items) => {
    // update selections if changes were made to the program dropdown
    setSelectedVehicleGroup(null);
    const cleanedItems = new Set(
      items.map((item) => item.split('-')[0].trim()),
    );
    const newSelections = {};
    Object.entries(selections).forEach(([key, value]) => {
      const programName = getProgramNameForVehicleGroup(key);
      if (cleanedItems.has(programName)) {
        newSelections[key] = value;
      }
    });
    setSelections(newSelections);
    if (items) {
      setSelectedPrograms(items);
    }
  };

  if (
    sinClassificationLoading ||
    solicitationLoading ||
    selectedStandardItems === null
  )
    return (
      <div className="height-mobile">
        <Spinner />
      </div>
    );

  return (
    <div className="margin-bottom-8">
      <SinSelectionHeader solicitation={solicitation} />
      <div className="grid-row flex-row flex-wrap">
        <div className=" grid-col flex-2">
          <Label
            className="text-bold"
            aria-label={selectedStandardItems}
            required
          >
            Program name
          </Label>
          <SolicitationProgramMultiselect
            programDropdownOptions={programDropdownOptions}
            selectedPrograms={selectedPrograms}
            handleProgramDropdownChange={handleProgramDropdownChange}
          />
        </div>
        <div className="padding-top-20 flex-align-self-end">
          <span className="text-bold" aria-label={selectedStandardItems}>
            {selectedStandardItems}
          </span>
          <span className="padding-left-1" aria-label="Standard Items selected">
            Standard Items selected
          </span>
        </div>
      </div>

      <div className="columns-wrapper grid-row flex-row margin-top-5 grid-gap-3">
        <div className="grid-col flex-4">
          <div className="header text-primary">
            <span aria-label="Selected programs">Selected programs</span>
          </div>
          {selectedPrograms?.length > 0 ? (
            <>
              {selectedPrograms?.map((item) => (
                <Accordion
                  key={item}
                  items={[
                    {
                      title: item,
                      content: (
                        <div>
                          {vehicleClassifications.map((vehicle) => {
                            const programName = item?.split('-')[0].trim();
                            if (
                              vehicle.parent === programName &&
                              vehicle.uniqueKey === 'fedStandardCodes'
                            ) {
                              return (
                                <ul key={vehicle.id} className="usa-sidenav">
                                  <li className="">
                                    <a
                                      style={{ cursor: 'pointer' }}
                                      className={
                                        selectedVehicleGroup === vehicle.value
                                          ? 'usa-current'
                                          : ''
                                      }
                                      onClick={() => {
                                        setSelectedVehicleGroup(vehicle.value);
                                      }}
                                      aria-label={vehicle.label.split('-')[1]}
                                    >
                                      {vehicle.label}
                                      {selections[vehicle.value]?.length > 0 ? (
                                        <CounterTag
                                          count={
                                            selections[vehicle.value]?.length
                                          }
                                          className="margin-left-1"
                                        />
                                      ) : null}
                                    </a>
                                  </li>
                                </ul>
                              );
                            }
                            return null;
                          })}
                        </div>
                      ),
                    },
                  ]}
                />
              ))}
            </>
          ) : (
            <EmptyState
              bottomText="Select one or more program names to start selecting related federal standard codes."
              containerStyles="padding-y-6 margin-top-1"
              hasBackground
            />
          )}
        </div>
        <div className="grid-col flex-8">
          <div className="header text-primary grid-row flex-justify-self-start flex-align-baseline">
            <span aria-label="Selected programs">Standard items</span>
            {selectedVehicleGroup?.length > 0 ? (
              <Checkbox
                checked={selectAllChecked}
                label="Select all"
                className="text-checkbox margin-left-2"
                data-testid="select-all-checkbox"
                onChange={() => {
                  setSelectAllChecked(!selectAllChecked);
                  handleSelectAllChange(!selectAllChecked);
                }}
              />
            ) : null}
          </div>
          {selectedVehicleGroup ? (
            Object.keys(groupedStandardItems).map((group, index) => {
              const standarditems = groupedStandardItems[group];
              const { title } = standarditems[0].vehicleTypeCode;
              const content = (
                <MultiSelect
                  data-testid={`solicitation-std-item-multiselect-${index}`}
                  options={standarditems.map((v) => {
                    return {
                      label: `${v.standardItemNumber} - ${v.title}`,
                      value: v.id,
                    };
                  })}
                  onChange={(v) =>
                    handleSelectStandardItem(v, selectedVehicleGroup)
                  }
                  selectedValues={selections[selectedVehicleGroup] || []}
                />
              );

              return (
                <div className="select-standard-item-accordion" key={title}>
                  <Accordion
                    multiselectable
                    bordered
                    className="margin-top-1"
                    items={[
                      {
                        title,
                        content,
                      },
                    ]}
                  />
                </div>
              );
            })
          ) : (
            <EmptyState
              bottomText="Select a program name and federal standard code to view related standard Items."
              containerStyles="padding-y-7 margin-top-1"
              hasBackground
            />
          )}
        </div>
      </div>
      <div className="grid-row margin-top-6">
        <Button
          variant="outline"
          label="Previous"
          data-testid="back-button"
          leftIcon={{ name: 'arrow_back' }}
          onClick={onClickPrev}
        />
        <Button
          variant="primary"
          label="Save"
          data-testid="select-sin-save-button"
          className="margin-left-1"
          onClick={() => onSave(false)}
        />
        <Button
          variant="primary"
          label="Save and continue"
          data-testid="select-sin-save-continue-button"
          className="margin-left-1"
          rightIcon={{ name: 'arrow_forward' }}
          onClick={() => onSave(true)}
        />
      </div>
    </div>
  );
};

export default EditSelectStandardItemWrapper;
